import axios from 'axios';
import i18n from '../i18n';

const resolveApiUrl = () => {
  // Highest priority: explicit override
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

  const deployEnv = (process.env.REACT_APP_DEPLOY_ENV || '').toLowerCase();
  if (deployEnv === 'prod' && process.env.REACT_APP_API_URL_PROD) {
    return process.env.REACT_APP_API_URL_PROD;
  }
  if (deployEnv === 'dev' && process.env.REACT_APP_API_URL_DEV) {
    return process.env.REACT_APP_API_URL_DEV;
  }

  // Safe default: same-origin
  return '/api/';
};

const ensureTrailingSlash = (url) => {
  if (!url) return url;
  return url.endsWith('/') ? url : `${url}/`;
};

const API_URL = ensureTrailingSlash(resolveApiUrl());

export const axiosClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshHttpClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

function readTokenFromStorage() {
  let token = localStorage.getItem('access_token');

  if (!token) {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        token = userData.token;
      } catch (e) {
        console.error('Failed to parse user data from localStorage', e);
      }
    }
  }

  return token;
}

function readRefreshTokenFromStorage() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (refreshToken) return refreshToken;

  const userStr = localStorage.getItem('user');
  if (!userStr) return null;

  try {
    const userData = JSON.parse(userStr);
    return userData.refresh_token;
  } catch (e) {
    console.error('Failed to parse user data from localStorage', e);
    return null;
  }
}

function persistTokens({ access, refresh }) {
  if (access) localStorage.setItem('access_token', access);
  if (refresh) localStorage.setItem('refresh_token', refresh);

  const userStr = localStorage.getItem('user');
  if (!userStr) return;

  try {
    const userData = JSON.parse(userStr);
    if (access) userData.token = access;
    if (refresh) userData.refresh_token = refresh;
    localStorage.setItem('user', JSON.stringify(userData));
  } catch (e) {
    console.error('Failed to parse user data from localStorage', e);
  }
}

function clearTokens() {
  localStorage.removeItem('user');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

let refreshPromise = null;
let isHardReloading = false;

/**
 * Helper to force a hard redirect to the landing page if session is lost.
 * Prevents multiple concurrent redirects and ignores intentional logouts.
 */
const forceLogoutRedirect = (reason) => {
  if (isHardReloading) return;
  
  const isLoggingOut = sessionStorage.getItem('is_logging_out') === 'true';
  if (isLoggingOut) {
    safeLog('intercepted-401-during-intentional-logout', { reason });
    return;
  }

  if (window.location.pathname !== '/') {
    isHardReloading = true;
    safeLog('forcing-hard-logout-redirect', { reason, from: window.location.pathname });
    window.location.href = '/';
  }
};

function safeLog(...args) {
  // Keep logs easy to filter
  console.log('[GLOW][axios]', ...args);
}

axiosClient.interceptors.request.use(
  (config) => {
    config.metadata = config.metadata ?? {};
    config.metadata.startTime = Date.now();

    const isFormDataRequest =
      typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isFormDataRequest) {
      config.headers = config.headers ?? {};
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }

    const token = readTokenFromStorage();
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add Accept-Language header based on current i18n language
    const currentLanguage = i18n.language || 'fr';
    config.headers = config.headers ?? {};
    config.headers['Accept-Language'] = currentLanguage;

    safeLog('request', {
      method: (config.method || 'GET').toUpperCase(),
      url: `${config.baseURL || ''}${config.url || ''}`,
      hasAuth: Boolean(token),
      language: currentLanguage,
    });
    return config;
  },
  (error) => Promise.reject(error)
);

axiosClient.interceptors.response.use(
  (response) => {
    const startedAt = response?.config?.metadata?.startTime;
    const durationMs = typeof startedAt === 'number' ? Date.now() - startedAt : undefined;
    safeLog('response', {
      method: (response.config?.method || 'GET').toUpperCase(),
      url: `${response.config?.baseURL || ''}${response.config?.url || ''}`,
      status: response.status,
      durationMs,
    });
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    const startedAt = originalRequest?.metadata?.startTime;
    const durationMs = typeof startedAt === 'number' ? Date.now() - startedAt : undefined;
    safeLog('error', {
      method: (originalRequest?.method || 'GET').toUpperCase(),
      url: `${originalRequest?.baseURL || ''}${originalRequest?.url || ''}`,
      status,
      durationMs,
    });

    if (!originalRequest || status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (originalRequest.url && (originalRequest.url.includes('auth/token/refresh') || originalRequest.url.includes('auth/logout'))) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    const refreshToken = readRefreshTokenFromStorage();
    const isLoggingOut = sessionStorage.getItem('is_logging_out') === 'true';

    if (!refreshToken) {
      safeLog('401-without-refresh-token', {
        url: `${originalRequest?.baseURL || ''}${originalRequest?.url || ''}`,
      });
      clearTokens();
      forceLogoutRedirect('no-refresh-token');
      return Promise.reject(error);
    }

    try {
      if (!refreshPromise) {
        safeLog('refresh-start');
        refreshPromise = refreshHttpClient
          .post('/auth/token/refresh/', { refresh: refreshToken })
          .then((res) => {
            const refreshData = res?.data;
            persistTokens({ access: refreshData?.access, refresh: refreshData?.refresh });
            safeLog('refresh-success');
            return refreshData;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      const refreshData = await refreshPromise;

      if (!refreshData?.access) {
        safeLog('refresh-failed-no-access');
        clearTokens();
        forceLogoutRedirect('refresh-failed');
        return Promise.reject(error);
      }

      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${refreshData.access}`;

      safeLog('retry-after-refresh', {
        method: (originalRequest?.method || 'GET').toUpperCase(),
        url: `${originalRequest?.baseURL || ''}${originalRequest?.url || ''}`,
      });

      return axiosClient(originalRequest);
    } catch (refreshError) {
      safeLog('refresh-error', refreshError?.response?.status || refreshError?.message);
      clearTokens();
      forceLogoutRedirect('refresh-exception');
      return Promise.reject(refreshError);
    }
  }
);
